import { FeedbackDialog } from '@ori-ui/components';
import { Button, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import React from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useTranslations } from '../../hooks/useTranslations_simplified-registration-BrandPartner';
import type { IConfirmDialogProps } from '../../interfaces';
import { TranslationKey } from '../../models';

const StyledFeedbackDialog = styled(FeedbackDialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxWidth: '600px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      position: 'absolute',
      bottom: 0,
      margin: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
}));

const ButtonDelete = styled(Button)(({ theme }) => ({
  background: theme.palette.error.main,
  color: theme.palette.primary.contrastText,
  '&:hover, &:focus': {
    background: theme.palette.error.main,
    color: theme.palette.primary.contrastText,
  },
}));

const ButtonCancel = styled(Button)(({ theme }) => ({
  textDecoration: 'underline',
  '&:hover, &:focus': {
    background: 'inherit',
    textDecoration: 'underline',
    boxShadow: 'none',
  },
}));

const children = (props: IConfirmDialogProps) => <div>{props.children}</div>;

const withConfirm =
  <P extends IConfirmDialogProps>(WrappedComponent: React.ComponentType<P>) =>
  (props: P) => {
    const { opened, handleConfirm, handleCancel } = props;
    const { formatMessage } = useTranslations();
    const { getTestIdProps } = useTestId();

    return (
      <StyledFeedbackDialog
        hideCloseButton
        color="error"
        severity="error"
        header={formatMessage(TranslationKey.warning)}
        open={opened}
        actions={[
          <ButtonDelete
            key="accept"
            color="inherit"
            onClick={handleConfirm}
            {...getTestIdProps('confirm-delete-dialog-btn-confirm')}
          >
            <Localization
              id={TranslationKey.delete}
              defaultMessage={defaultTranslations.delete}
            />
          </ButtonDelete>,
          <ButtonCancel
            key="cancel"
            color="inherit"
            onClick={handleCancel}
            {...getTestIdProps('confirm-delete-dialog-btn-cancel')}
          >
            <Localization
              id={TranslationKey.cancel}
              defaultMessage={defaultTranslations.cancel}
            />
          </ButtonCancel>,
        ]}
      >
        <WrappedComponent {...props} />
      </StyledFeedbackDialog>
    );
  };

export const ConfirmDeleteDialog = withConfirm(children);
